<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <div>
          <h3>智能插座配置</h3>
          <p class="device-subtitle">{{ deviceData?.deviceName || '-' }}</p>
        </div>
        <button @click="handleClose" class="close-btn">&times;</button>
      </div>

      <div class="modal-body">
        <form class="device-form" @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="mqttHost" class="required">MQTT地址</label>
            <input id="mqttHost" v-model="formData.mqttHost" type="text" class="form-input" placeholder="如 mqtt.smart-bird.cn" :class="{ error: errors.mqttHost }" />
            <span v-if="errors.mqttHost" class="error-message">{{ errors.mqttHost }}</span>
          </div>

          <div class="form-group">
            <label for="mqttPort" class="required">MQTT端口</label>
            <input id="mqttPort" v-model.number="formData.mqttPort" type="number" min="1" class="form-input" placeholder="如 1883" :class="{ error: errors.mqttPort }" />
            <span v-if="errors.mqttPort" class="error-message">{{ errors.mqttPort }}</span>
          </div>

          <div class="form-group">
            <label for="mqttClientId" class="required">Client ID（开发设置调试）</label>
            <input id="mqttClientId" v-model="formData.mqttClientId" type="text" class="form-input" placeholder="请输入控制台开发设置里的 Client ID" :class="{ error: errors.mqttClientId }" />
            <span v-if="errors.mqttClientId" class="error-message">{{ errors.mqttClientId }}</span>
          </div>

          <div class="form-group">
            <label for="mqttUsername" class="required">用户名</label>
            <input id="mqttUsername" v-model="formData.mqttUsername" type="text" class="form-input" placeholder="请输入控制台开发设置里的用户名" :class="{ error: errors.mqttUsername }" />
            <span v-if="errors.mqttUsername" class="error-message">{{ errors.mqttUsername }}</span>
          </div>

          <div class="form-group">
            <label for="mqttPassword" class="required">密码</label>
            <input id="mqttPassword" v-model="formData.mqttPassword" type="text" class="form-input" placeholder="请输入控制台开发设置里的密码" :class="{ error: errors.mqttPassword }" />
            <span v-if="errors.mqttPassword" class="error-message">{{ errors.mqttPassword }}</span>
          </div>

          <div class="form-group">
            <label for="deviceIdentifier" class="required">设备标识（客户ID）</label>
            <input id="deviceIdentifier" v-model="formData.deviceIdentifier" type="text" class="form-input" placeholder="请输入设备管理弹窗里的客户ID" :class="{ error: errors.deviceIdentifier }" />
            <span v-if="errors.deviceIdentifier" class="error-message">{{ errors.deviceIdentifier }}</span>
          </div>

          <div class="topic-help">
            <p>注意：平台页面中的“订阅主题 / 发布主题”是按设备视角命名的，外部客户端接入时请勿按字面理解填写。</p>
            <p>设备详情里显示的“订阅主题”表示设备会订阅该主题，因此客户端应向该主题发布控制指令；设备详情里显示的“发布主题”表示设备会向该主题上报数据，因此客户端应订阅该主题接收设备消息。</p>
            <p>请按下面规则填写：</p>
            <p>1. 控制主题：反填平台页面里的“订阅主题”。常见形态是 <code>/.../publish</code>。这是系统给插座发送计时通断电指令的主题。</p>
            <p>2. 上报主题：反填平台页面里的“发布主题”。常见形态是 <code>/.../subscribe</code>。这是设备执行后主动回包、上报状态和功率数据的主题。</p>
            <p>例如平台页面若显示：</p>
            <p><code>订阅主题：/lSavBf/.../c82b96f737ab/publish</code></p>
            <p><code>发布主题：/lSavBf/.../c82b96f737ab/subscribe</code></p>
            <p>那么本弹窗中应填写为：</p>
            <p><code>设备发布主题(topicDevicePublish) = /lSavBf/.../c82b96f737ab/subscribe</code></p>
            <p><code>设备订阅主题(topicDeviceSubscribe) = /lSavBf/.../c82b96f737ab/publish</code></p>
            <p>从客户端视角看，这会像“反填”：客户端订阅页面的“发布主题”，客户端发布到页面的“订阅主题”；如果把 <code>/publish</code> 和 <code>/subscribe</code> 按字面方向填反，就会出现设备在线但指令无响应。</p>
          </div>

          <div class="form-group">
            <label for="topicDevicePublish" class="required">设备发布主题（GeekOpen“发布主题”，通常以 /subscribe 结尾，客户端订阅这里）</label>
            <input id="topicDevicePublish" v-model="formData.topicDevicePublish" type="text" class="form-input" placeholder="填写 GeekOpen 页面“发布主题”，如 /xxx/.../subscribe" :class="{ error: errors.topicDevicePublish }" />
            <span v-if="errors.topicDevicePublish" class="error-message">{{ errors.topicDevicePublish }}</span>
          </div>

          <div class="form-group">
            <label for="topicDeviceSubscribe" class="required">设备订阅主题（GeekOpen“订阅主题”，通常以 /publish 结尾，客户端向这里发布）</label>
            <input id="topicDeviceSubscribe" v-model="formData.topicDeviceSubscribe" type="text" class="form-input" placeholder="填写 GeekOpen 页面“订阅主题”，如 /xxx/.../publish" :class="{ error: errors.topicDeviceSubscribe }" />
            <span v-if="errors.topicDeviceSubscribe" class="error-message">{{ errors.topicDeviceSubscribe }}</span>
          </div>

          <div class="form-group">
            <label for="smartPlugCycleEnabled">是否启用空闲循环断电</label>
            <select id="smartPlugCycleEnabled" v-model="formData.smartPlugCycleEnabled" class="form-select">
              <option :value="false">关闭</option>
              <option :value="true">开启</option>
            </select>
            <div class="form-help">关闭后不需要填写“每隔多少分钟断电一次”，系统不会自动循环断电。</div>
          </div>

          <div class="form-group" v-if="formData.smartPlugCycleEnabled">
            <label for="smartPlugCycleIntervalMinutes" class="required">每隔多少分钟断电一次</label>
            <input id="smartPlugCycleIntervalMinutes" v-model.number="formData.smartPlugCycleIntervalMinutes" type="number" min="1" class="form-input" placeholder="请输入分钟数" :class="{ error: errors.smartPlugCycleIntervalMinutes }" />
            <span v-if="errors.smartPlugCycleIntervalMinutes" class="error-message">{{ errors.smartPlugCycleIntervalMinutes }}</span>
          </div>

          <div class="form-group">
            <label for="smartPlugPowerOffSeconds" class="required">断电后多少秒恢复通电</label>
            <input id="smartPlugPowerOffSeconds" v-model.number="formData.smartPlugPowerOffSeconds" type="number" min="1" class="form-input" placeholder="请输入秒数" :class="{ error: errors.smartPlugPowerOffSeconds }" />
            <span v-if="errors.smartPlugPowerOffSeconds" class="error-message">{{ errors.smartPlugPowerOffSeconds }}</span>
          </div>

        </form>
      </div>

      <div class="modal-footer">
        <button @click="handleClose" class="cancel-btn">取消</button>
        <button @click="handleSubmit" class="submit-btn" :disabled="loading">
          {{ loading ? '保存中...' : '保存配置' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'EditSocketConfigModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    deviceData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      loading: false,
      errors: {},
      formData: {
        mqttHost: '',
        mqttPort: 1883,
        mqttClientId: '',
        mqttUsername: '',
        mqttPassword: '',
        deviceIdentifier: '',
        topicDevicePublish: '',
        topicDeviceSubscribe: '',
        smartPlugCycleEnabled: false,
        smartPlugCycleIntervalMinutes: 30,
        smartPlugPowerOffSeconds: 60
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initFormData()
      }
    },
    deviceData: {
      deep: true,
      handler() {
        if (this.visible) {
          this.initFormData()
        }
      }
    }
  },
  methods: {
    initFormData() {
      this.formData = {
        mqttHost: this.deviceData?.mqttHost || '',
        mqttPort: this.deviceData?.mqttPort || 1883,
        mqttClientId: this.deviceData?.mqttClientId || '',
        mqttUsername: this.deviceData?.mqttUsername || '',
        mqttPassword: this.deviceData?.mqttPassword || '',
        deviceIdentifier: this.deviceData?.deviceIdentifier || '',
        topicDevicePublish: this.deviceData?.topicDevicePublish || '',
        topicDeviceSubscribe: this.deviceData?.topicDeviceSubscribe || '',
        smartPlugCycleEnabled: Boolean(this.deviceData?.smartPlugCycleEnabled),
        smartPlugCycleIntervalMinutes: this.deviceData?.smartPlugCycleIntervalMinutes || 30,
        smartPlugPowerOffSeconds: this.deviceData?.smartPlugPowerOffSeconds || 60
      }
      this.errors = {}
      this.loading = false
    },
    validateForm() {
      this.errors = {}
      let isValid = true
      const requiredFields = [
        ['mqttHost', '请输入 MQTT 地址'],
        ['mqttPort', '请输入正确的 MQTT 端口'],
        ['mqttClientId', '请输入 Client ID'],
        ['mqttUsername', '请输入用户名'],
        ['mqttPassword', '请输入密码'],
        ['deviceIdentifier', '请输入设备标识'],
        ['topicDevicePublish', '请输入设备发布主题'],
        ['topicDeviceSubscribe', '请输入设备订阅主题']
      ]

      requiredFields.forEach(([field, message]) => {
        const value = this.formData[field]
        if (field === 'mqttPort') {
          if (!value || Number(value) <= 0) {
            this.errors[field] = message
            isValid = false
          }
        } else if (!value || !String(value).trim()) {
          this.errors[field] = message
          isValid = false
        }
      })

      if (this.formData.smartPlugCycleEnabled) {
        if (!this.formData.smartPlugCycleIntervalMinutes || this.formData.smartPlugCycleIntervalMinutes <= 0) {
          this.errors.smartPlugCycleIntervalMinutes = '请输入大于 0 的循环间隔'
          isValid = false
        }
      }
      if (!this.formData.smartPlugPowerOffSeconds || this.formData.smartPlugPowerOffSeconds <= 0) {
        this.errors.smartPlugPowerOffSeconds = '请输入大于 0 的恢复秒数'
        isValid = false
      }

      return isValid
    },
    buildPayload() {
      return {
        mqttHost: this.formData.mqttHost.trim(),
        mqttPort: Number(this.formData.mqttPort),
        mqttClientId: this.formData.mqttClientId.trim(),
        mqttUsername: this.formData.mqttUsername.trim(),
        mqttPassword: this.formData.mqttPassword.trim(),
        deviceIdentifier: this.formData.deviceIdentifier.trim(),
        topicDevicePublish: this.formData.topicDevicePublish.trim(),
        topicDeviceSubscribe: this.formData.topicDeviceSubscribe.trim(),
        smartPlugCycleEnabled: this.formData.smartPlugCycleEnabled,
        smartPlugCycleIntervalMinutes: this.formData.smartPlugCycleEnabled
          ? Number(this.formData.smartPlugCycleIntervalMinutes)
          : null,
        smartPlugPowerOffSeconds: Number(this.formData.smartPlugPowerOffSeconds)
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) return
      this.loading = true
      try {
        const response = await request.put(`/api/devices/${this.deviceData.id}/socket-config`, this.buildPayload())
        if (response.data.success && response.data.code === 200) {
          this.$emit('success', response.data.data)
          this.handleClose()
          return
        }
        throw new Error(response.data.message || '保存配置失败')
      } catch (error) {
        alert(error.response?.data?.message || error.message || '保存配置失败')
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.$emit('close')
    },
    handleOverlayClick() {
      if (!this.loading) {
        this.handleClose()
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 640px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
  color: #333;
}

.device-subtitle {
  margin: 0;
  font-size: 13px;
  color: #999;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
}

.modal-body {
  padding: 24px;
  max-height: 68vh;
  overflow-y: auto;
}

.device-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #ff4d4f;
}

.form-input,
.form-select {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.form-input.error,
.form-select.error {
  border-color: #ff4d4f;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
}

.form-help {
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 6px;
}

.topic-help {
  padding: 12px 14px;
  border-radius: 6px;
  background: #f6fbff;
  border: 1px solid #d6ecff;
  color: #35526b;
  font-size: 13px;
  line-height: 1.6;
}

.topic-help p {
  margin: 0;
}

.topic-help p + p {
  margin-top: 6px;
}

.test-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.test-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.test-actions {
  display: flex;
  gap: 12px;
}

.test-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: white;
}

.power-off-btn {
  background: #ff4d4f;
}

.power-on-btn {
  background: #52c41a;
}

.test-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.test-message {
  margin: 12px 0 0;
  font-size: 13px;
}

.test-message.success {
  color: #52c41a;
}

.test-message.error {
  color: #ff4d4f;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.cancel-btn,
.submit-btn {
  padding: 8px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.cancel-btn {
  border: 1px solid #d9d9d9;
  background: white;
  color: #666;
}

.submit-btn {
  border: 1px solid #1890ff;
  background: #1890ff;
  color: white;
}

.submit-btn:disabled {
  background: #d9d9d9;
  border-color: #d9d9d9;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
  }

  .test-actions,
  .modal-footer {
    flex-direction: column;
  }
}
</style>
